// src/lib/resilientFetch.ts
// Cliente de fetch resiliente com retry, backoff exponencial, jitter e circuit breaker

export type ResilientFetchError = {
  name: "ResilientFetchError";
  message: string;
  status?: number;
  code?: string;
  isRetryable: boolean;
  cause?: unknown;
};

type CircuitState = {
  failures: number;
  openUntil: number; // timestamp ms
};

const circuit: CircuitState = { failures: 0, openUntil: 0 };

function now() {
  return Date.now();
}

function isCircuitOpen() {
  return now() < circuit.openUntil;
}

function openCircuit(ms: number) {
  circuit.openUntil = now() + ms;
}

export function resetCircuit() {
  circuit.failures = 0;
  circuit.openUntil = 0;
}

function incFailure() {
  circuit.failures += 1;
}

export function getCircuitState() {
  return {
    isOpen: isCircuitOpen(),
    failures: circuit.failures,
    openUntil: circuit.openUntil,
    remainingMs: Math.max(0, circuit.openUntil - now()),
  };
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function jitter(ms: number) {
  // jitter simples +/- 25%
  const delta = ms * 0.25;
  return Math.max(0, Math.round(ms - delta + Math.random() * (2 * delta)));
}

function parseRetryAfter(headers: Headers): number | null {
  const ra = headers.get("retry-after");
  if (!ra) return null;

  // pode vir segundos ou data HTTP
  const seconds = Number(ra);
  if (!Number.isNaN(seconds)) return Math.max(0, seconds * 1000);

  const date = Date.parse(ra);
  if (!Number.isNaN(date)) return Math.max(0, date - now());

  return null;
}

export function toResilientError(input: Partial<ResilientFetchError>): ResilientFetchError {
  return {
    name: "ResilientFetchError",
    message: input.message ?? "Falha ao conectar no servidor.",
    status: input.status,
    code: input.code,
    isRetryable: input.isRetryable ?? false,
    cause: input.cause,
  };
}

// Heurística: tudo que for "infra" => retryable
export function isRetryableStatus(status?: number) {
  if (!status) return true; // sem status geralmente é network error
  return status === 503 || status === 502 || status === 504 || status === 429;
}

// Detectar erros de conexão/rede em mensagens de erro
export function isNetworkError(error: unknown): boolean {
  if (!error) return false;
  const message = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  return (
    message.includes("503") ||
    message.includes("502") ||
    message.includes("504") ||
    message.includes("fetch") ||
    message.includes("network") ||
    message.includes("connection") ||
    message.includes("timeout") ||
    message.includes("econnrefused") ||
    message.includes("enotfound") ||
    message.includes("upstream") ||
    message.includes("unavailable") ||
    message.includes("failed to fetch") ||
    message.includes("load failed") ||
    message.includes("authretryablefetcherror")
  );
}

export async function resilientFetch(
  url: string,
  init: RequestInit & {
    timeoutMs?: number;
    maxRetries?: number;
    baseDelayMs?: number;
    circuitOpenMs?: number;
  } = {}
): Promise<Response> {
  const {
    timeoutMs = 15000,
    maxRetries = 3,
    baseDelayMs = 800,
    circuitOpenMs = 15000,
    ...requestInit
  } = init;

  // Se browser offline, nem tenta
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    throw toResilientError({
      message: "Você está sem conexão com a internet.",
      code: "OFFLINE",
      isRetryable: true,
    });
  }

  if (isCircuitOpen()) {
    throw toResilientError({
      message: "Servidor temporariamente indisponível. Tentando reconectar…",
      code: "CIRCUIT_OPEN",
      isRetryable: true,
    });
  }

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const res = await fetch(url, { ...requestInit, signal: controller.signal });
      clearTimeout(id);

      if (res.ok) {
        resetCircuit();
        return res;
      }

      // Se não ok, decide retry
      const retryable = isRetryableStatus(res.status);
      if (!retryable) {
        // não retry: erro "real" (401, 403, 400…)
        throw toResilientError({
          message: `Erro do servidor (${res.status}).`,
          status: res.status,
          isRetryable: false,
        });
      }

      // retryable: 503/502/504/429
      incFailure();

      // Se falhou demais, abre circuito por um tempo
      if (circuit.failures >= 3) openCircuit(circuitOpenMs);

      // se acabou as tentativas
      if (attempt === maxRetries) {
        throw toResilientError({
          message: "Servidor temporariamente indisponível. Tente novamente em instantes.",
          status: res.status,
          isRetryable: true,
        });
      }

      const ra = parseRetryAfter(res.headers);
      const delay = ra ?? jitter(baseDelayMs * Math.pow(2, attempt));
      await sleep(delay);
      continue;
    } catch (err: unknown) {
      clearTimeout(id);
      lastError = err;

      // Se já é um ResilientFetchError, propaga
      if ((err as ResilientFetchError)?.name === "ResilientFetchError") {
        throw err;
      }

      // AbortController timeout
      const isAbort = (err as Error)?.name === "AbortError";

      incFailure();
      if (circuit.failures >= 3) openCircuit(circuitOpenMs);

      if (attempt === maxRetries) {
        throw toResilientError({
          message: isAbort
            ? "Servidor demorou para responder. Tente novamente."
            : "Falha de conexão com o servidor. Tente novamente.",
          code: isAbort ? "TIMEOUT" : "NETWORK",
          isRetryable: true,
          cause: lastError,
        });
      }

      const delay = jitter(baseDelayMs * Math.pow(2, attempt));
      await sleep(delay);
    }
  }

  throw toResilientError({
    message: "Falha inesperada.",
    isRetryable: true,
    cause: lastError,
  });
}
